import {
  shouldShowRiskFlowDirectionalForecast,
  type RiskFlowAlert,
} from "./riskflow-feed";
import {
  RISKFLOW_ECON_EVENT_DEFINITIONS,
  type RiskFlowEconEventDefinition,
} from "./riskflow-econ-print-events";

export type RiskFlowEconPrintResult = "beat" | "miss" | "inline";
export type RiskFlowEconDirectionalRead = "bullish" | "bearish" | "neutral";

export interface RiskFlowEconPrintValue {
  raw: string;
  display: string;
  numeric: number | null;
  unit: string | null;
}

export interface RiskFlowEconPrintAnalysis {
  key: string;
  title: string;
  printedAt: string | null;
  instrument: string;
  actual: RiskFlowEconPrintValue | null;
  forecast: RiskFlowEconPrintValue | null;
  previous: RiskFlowEconPrintValue | null;
  result: RiskFlowEconPrintResult;
  surprisePercent: number | null;
  sigma: number | null;
  directionalRead: RiskFlowEconDirectionalRead;
}

const VALUE_PATTERN =
  "([+\\-−—]?\\s*(?:\\d+(?:[\\d,.]*\\d)?|\\.\\d+)(?:\\s?(?:K|M|B|T|%|bps|bp))?|N/?A|--|-|—)";

export function parseRiskFlowEconPrint(
  alert: RiskFlowAlert,
): RiskFlowEconPrintAnalysis | null {
  const text = `${alert.headline} ${alert.summary ?? ""}`.trim();
  if (!/\bactual\b/i.test(text) || !/\bforecast\b/i.test(text)) return null;

  const definition = inferEventDefinition(text);
  if (!definition) return null;

  const actual = readValue(text, "Actual", alert.econData?.actual);
  const forecast = readValue(text, "Forecast", alert.econData?.forecast);
  const previous = readValue(text, "Previous", alert.econData?.previous);
  if (!actual || !forecast) return null;

  const result =
    normalizeBeatMiss(alert.econData?.beatMiss ?? null) ??
    beatMiss(actual.numeric, forecast.numeric);
  const surprisePercent =
    finite(alert.econData?.surprisePercent) ??
    percentSurprise(actual.numeric, forecast.numeric);
  const sigma = estimateSigma(
    actual.numeric,
    forecast.numeric,
    previous?.numeric,
  );
  const instrument = normalizeInstrument(alert.instrument);
  const directionalRead = directionalReadForPrint({
    alert,
    result,
    lowerIsBullish: definition.lowerIsBullish === true,
  });

  return {
    key: definition.key,
    title: titleFromHeadline(alert.headline) ?? definition.title,
    printedAt: alert.publishedAt ?? null,
    instrument,
    actual,
    forecast,
    previous,
    result,
    surprisePercent,
    sigma,
    directionalRead,
  };
}

export function resultToneClass(
  read: RiskFlowEconDirectionalRead | RiskFlowEconPrintResult,
): string {
  if (read === "bullish" || read === "beat") {
    return "text-[var(--fintheon-bullish)]";
  }
  if (read === "bearish" || read === "miss") return "text-[#ff4d57]";
  return "text-[var(--fintheon-accent)]";
}

export function riskFlowEconAiRead(
  analysis: RiskFlowEconPrintAnalysis,
): string {
  if (isTamePrint(analysis)) return nothingburgerRead(analysis);
  const result = displayResult(analysis.result).toLowerCase();
  if (analysis.directionalRead === "neutral") {
    if ((analysis.sigma ?? 0) >= 3.5) return "shock risk, watch reaction";
    if (Math.abs(analysis.surprisePercent ?? 0) >= 5) {
      return `${result} with teeth`;
    }
    return `${result}, watch reaction`;
  }
  const direction = displayDirection(analysis.directionalRead).toLowerCase();
  if ((analysis.sigma ?? 0) >= 3.5) return `${direction} shock risk`;
  if (Math.abs(analysis.surprisePercent ?? 0) >= 5) {
    return `${result} with teeth`;
  }
  return `${result}, watch ${direction}`;
}

export function riskFlowEconAiReadToneClass(
  analysis: RiskFlowEconPrintAnalysis,
): string {
  return isTamePrint(analysis)
    ? "text-[var(--fintheon-text)]/58"
    : resultToneClass(analysis.directionalRead);
}

function inferEventDefinition(
  text: string,
): RiskFlowEconEventDefinition | null {
  return (
    RISKFLOW_ECON_EVENT_DEFINITIONS.find((definition) =>
      definition.patterns.some((pattern) => pattern.test(text)),
    ) ?? null
  );
}

function titleFromHeadline(headline: string): string | null {
  const [prefix] = headline.split(/\s+Actual\b/i);
  const title = prefix
    ?.replace(/\s+-\s+FinancialJuice.*$/i, "")
    .replace(/\s+-\s+FJElite.*$/i, "")
    .replace(/\s+/g, " ")
    .trim();
  return title || null;
}

function readValue(
  text: string,
  label: "Actual" | "Forecast" | "Previous",
  fallback?: number | null,
): RiskFlowEconPrintValue | null {
  const expression = new RegExp(
    `\\b${label}\\s*[:=]?\\s*${VALUE_PATTERN}`,
    "i",
  );
  const match = text.match(expression);
  if (match?.[1]) return parseValue(match[1]);
  const finiteFallback = finite(fallback);
  if (finiteFallback != null) return numericValue(finiteFallback);
  return null;
}

function parseValue(rawValue: string): RiskFlowEconPrintValue {
  const raw = rawValue.trim().replace(/\s+/g, "");
  if (!raw || /^(?:N\/?A|--|-|—)$/i.test(raw)) {
    return { raw, display: "—", numeric: null, unit: null };
  }
  const negative = /^[−—-]/.test(raw);
  const cleaned = raw.replace(/^[+−—-]/, "");
  const unit = cleaned.match(/[a-z%]+$/i)?.[0] ?? null;
  const numberPart = cleaned.replace(/[a-z%]+$/i, "").replace(/,/g, "");
  const parsed = Number(numberPart);
  const numeric = Number.isFinite(parsed)
    ? (negative ? -parsed : parsed) * unitMultiplier(unit)
    : null;
  return {
    raw,
    display: `${negative ? "-" : raw.startsWith("+") ? "+" : ""}${cleaned}`,
    numeric,
    unit: unit?.toUpperCase() ?? null,
  };
}

function numericValue(value: number): RiskFlowEconPrintValue {
  return {
    raw: String(value),
    display: String(value),
    numeric: value,
    unit: null,
  };
}

function unitMultiplier(unit: string | null): number {
  if (!unit) return 1;
  const normalized = unit.toUpperCase();
  if (normalized === "K") return 1_000;
  if (normalized === "M") return 1_000_000;
  if (normalized === "B") return 1_000_000_000;
  if (normalized === "T") return 1_000_000_000_000;
  return 1;
}

function normalizeBeatMiss(
  value: string | null,
): RiskFlowEconPrintResult | null {
  return value === "beat" || value === "miss" || value === "inline"
    ? value
    : null;
}

function beatMiss(
  actual: number | null,
  forecast: number | null,
): RiskFlowEconPrintResult {
  if (actual == null || forecast == null) return "inline";
  const diff = actual - forecast;
  const tolerance = Math.max(Math.abs(forecast) * 0.0025, 0.0001);
  if (Math.abs(diff) <= tolerance) return "inline";
  return diff > 0 ? "beat" : "miss";
}

function percentSurprise(
  actual: number | null,
  forecast: number | null,
): number | null {
  if (actual == null || forecast == null || forecast === 0) return null;
  return round(((actual - forecast) / Math.abs(forecast)) * 100, 2);
}

function estimateSigma(
  actual: number | null,
  forecast: number | null,
  previous: number | null | undefined,
): number | null {
  if (actual == null || forecast == null) return null;
  const diff = Math.abs(actual - forecast);
  const previousDelta = previous == null ? 0 : Math.abs(previous - forecast);
  const denominator = Math.max(
    previousDelta,
    Math.abs(forecast) * 0.01,
    0.0001,
  );
  return Math.min(6, round(diff / denominator, 1));
}

function directionalReadForPrint({
  alert,
  result,
  lowerIsBullish,
}: {
  alert: RiskFlowAlert;
  result: RiskFlowEconPrintResult;
  lowerIsBullish: boolean;
}): RiskFlowEconDirectionalRead {
  if (!shouldShowRiskFlowDirectionalForecast(alert)) return "neutral";
  if (alert.direction === "Bullish") return "bullish";
  if (alert.direction === "Bearish") return "bearish";
  if (result === "inline") return "neutral";
  if (lowerIsBullish) return result === "miss" ? "bullish" : "bearish";
  return result === "beat" ? "bullish" : "bearish";
}

const NOTHINGBURGER_READS = [
  "looks like a nothingburger",
  "probably safe to exhale",
  "tiny ripple, not a wave",
  "desk can keep the coffee warm",
  "market can shrug this one off",
] as const;

function isTamePrint(analysis: RiskFlowEconPrintAnalysis): boolean {
  const surprise = Math.abs(analysis.surprisePercent ?? 0);
  if (analysis.result === "inline") return true;
  if (analysis.sigma != null && analysis.sigma < 1.5) return true;
  return surprise < 2.5;
}

function nothingburgerRead(analysis: RiskFlowEconPrintAnalysis): string {
  const seed = `${analysis.key}:${analysis.printedAt ?? ""}:${
    analysis.actual?.raw ?? ""
  }`;
  let total = 0;
  for (let index = 0; index < seed.length; index += 1) {
    total += seed.charCodeAt(index);
  }
  return NOTHINGBURGER_READS[total % NOTHINGBURGER_READS.length];
}

function displayResult(value: RiskFlowEconPrintResult): string {
  return value === "inline"
    ? "In-line"
    : value[0].toUpperCase() + value.slice(1);
}

function displayDirection(value: RiskFlowEconDirectionalRead): string {
  return value === "neutral"
    ? "Neutral"
    : value[0].toUpperCase() + value.slice(1);
}

function normalizeInstrument(value: string | null | undefined): string {
  const raw = value?.trim();
  if (!raw) return "/NQ";
  return raw.startsWith("/") || raw.startsWith("$") ? raw : `/${raw}`;
}

function finite(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function round(value: number, decimals: number): number {
  const multiplier = 10 ** decimals;
  return Math.round(value * multiplier) / multiplier;
}
